// Днес (04 §1) — what changed, what is due, what to call next.
// Executive Morning Briefing Dashboard for the agency owner.

import { Link } from 'react-router-dom';
import {
  useCompleteFollowUp,
  useFollowUpsDue,
  useLastSeen,
  useOpenReviewCount,
  useSinceLastVisit,
  useTopUnworked,
  type UnworkedLead,
} from './useToday.js';
import {
  IconAdmin,
  IconCheck,
  IconDashboard,
  IconKanban,
  IconLeads,
  IconPhone,
  IconPipeline,
} from '../../components/Icons.js';
import { useNicheContext } from '../../lib/nicheContext.js';
import './today.css';

function FollowUpsSection() {
  const { data: followUps = [], error: followUpsError } = useFollowUpsDue();
  const completeFollowUp = useCompleteFollowUp();

  return (
    <section className="today-box">
      <div className="today-box__head">
        <div className="today-box__title-group">
          <span className="today-box__icon today-box__icon--phone">
            <IconPhone size={18} />
          </span>
          <div>
            <h2 className="today-box__title">Последващи обаждания за днес</h2>
            <p className="today-box__subtitle">Приоритетни разговори с настъпил падеж</p>
          </div>
        </div>
        <span className="badge badge--info">{followUps.length} за днес</span>
      </div>

      <div className="today-box__body">
        {followUpsError ? (
          <div className="empty-state">
            <p className="empty-state__title">Грешка при зареждане</p>
            <p className="empty-state__desc">{followUpsError.message}</p>
          </div>
        ) : followUps.length === 0 ? (
          <div className="empty-state">
            <p className="empty-state__title">Няма насрочени за днес</p>
            <p className="empty-state__desc">
              Всички насрочени разговори са обработени или няма поставени дати за днес.
            </p>
            <Link to="/leads" className="btn btn--secondary btn--sm">
              Разгледай потенциални
            </Link>
          </div>
        ) : (
          <div className="today-list">
            {followUps.map((item) => (
              <div key={item.id} className="today-item">
                <div className="today-item__main">
                  <div className="today-item__top">
                    <Link to={`/leads/${item.lead_id}`} className="today-item__name">
                      {item.business_name}
                    </Link>
                    <span className="today-item__time" title="Краен срок за обаждане">
                      {item.next_follow_up}
                    </span>
                  </div>
                  <div className="today-item__meta">
                    <span className="badge badge--neutral">{item.outcome}</span>
                    {item.notes && <span className="today-item__note">{item.notes}</span>}
                  </div>
                </div>
                <div className="today-item__actions">
                  <button
                    type="button"
                    className="btn btn--success btn--sm"
                    disabled={completeFollowUp.isPending}
                    onClick={() => completeFollowUp.mutate(item.id)}
                    title="Отбележи разговора като изпълнен"
                  >
                    <IconCheck size={14} />
                    <span>✓ Готово</span>
                  </button>
                  <Link
                    to={`/leads/${item.lead_id}/cue`}
                    className="btn btn--secondary btn--sm"
                    title="Отвори картонче за разговор"
                  >
                    Картонче
                  </Link>
                  <Link to={`/leads/${item.lead_id}`} className="btn btn--secondary btn--sm">
                    Отвори
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

interface TodayMetricsProps {
  followUpsCount: number;
  unworkedCount: number;
  newLeads: number;
  newDocuments?: number | undefined;
  openReviews: number;
}

function TodayMetrics({
  followUpsCount,
  unworkedCount,
  newLeads,
  newDocuments,
  openReviews,
}: TodayMetricsProps) {
  const followUpSubtitle =
    followUpsCount > 0 ? 'Насрочени обаждания за деня' : 'Няма насрочени за днес';
  const reviewSubtitle = openReviews > 0 ? 'Чакат ръчно потвърждение' : 'Всичко е проверено';
  const sinceSubtitle = newDocuments ? `${newDocuments} нови досиета` : 'Нови лийдове в базата';

  return (
    <div className="today-dash__metrics">
      <Link to="/leads" className="stat-card stat-card--interactive">
        <div className="stat-card__head">
          <span className="stat-card__label">Разговори днес</span>
          <IconPhone size={18} className="stat-card__icon" />
        </div>
        <div className="stat-card__val">{followUpsCount}</div>
        <div className="stat-card__sub">{followUpSubtitle}</div>
      </Link>

      <Link to="/leads?tier=A" className="stat-card stat-card--interactive">
        <div className="stat-card__head">
          <span className="stat-card__label">Топ готови обекти</span>
          <IconLeads size={18} className="stat-card__icon" />
        </div>
        <div className="stat-card__val">{unworkedCount}</div>
        <div className="stat-card__sub">Tier A/B с готов одит и досие</div>
      </Link>

      <Link to="/leads" className="stat-card stat-card--interactive">
        <div className="stat-card__head">
          <span className="stat-card__label">Ново от посещението</span>
          <IconDashboard size={18} className="stat-card__icon" />
        </div>
        <div className="stat-card__val">{newLeads}</div>
        <div className="stat-card__sub">{sinceSubtitle}</div>
      </Link>

      <Link to="/admin" className="stat-card stat-card--interactive">
        <div className="stat-card__head">
          <span className="stat-card__label">За проверка</span>
          <IconAdmin size={18} className="stat-card__icon" />
        </div>
        <div
          className="stat-card__val"
          style={{ color: openReviews > 0 ? 'var(--warn)' : 'var(--ok)' }}
        >
          {openReviews}
        </div>
        <div className="stat-card__sub">{reviewSubtitle}</div>
      </Link>
    </div>
  );
}

function TopUnworkedItem({ lead }: { lead: UnworkedLead }) {
  const location = lead.area ?? lead.city_key ?? '—';

  return (
    <div className="today-item">
      <div className="today-item__main">
        <div className="today-item__top">
          <Link to={`/leads/${lead.id}`} className="today-item__name">
            {lead.business_name}
          </Link>
          <div className="today-item__badges">
            <span className="badge badge--tier-a">Клас {lead.tier ?? 'A'}</span>
            <span className="badge badge--score">{lead.lead_score ?? 0} т.</span>
          </div>
        </div>
        <div className="today-item__meta">
          <span>📍 {location}</span>
        </div>
      </div>
      <div className="today-item__actions">
        <Link
          to={`/leads/${lead.id}/cue`}
          className="btn btn--secondary btn--sm"
          title="Бърз скрипт за разговор"
        >
          Картонче
        </Link>
        <Link to={`/leads/${lead.id}`} className="btn btn--primary btn--sm">
          <IconPhone size={14} />
          <span>Обади се</span>
        </Link>
      </div>
    </div>
  );
}

interface TopUnworkedSectionProps {
  unworked: UnworkedLead[];
  error: Error | null;
}

function TopUnworkedSection({ unworked, error }: TopUnworkedSectionProps) {
  return (
    <section className="today-box">
      <div className="today-box__head">
        <div className="today-box__title-group">
          <span className="today-box__icon today-box__icon--leads">
            <IconLeads size={18} />
          </span>
          <div>
            <h2 className="today-box__title">Най-добрите необработени (Tier A/B)</h2>
            <p className="today-box__subtitle">Готови за първи контакт с изготвено досие</p>
          </div>
        </div>
        <span className="badge badge--tier-a">{unworked.length} готови</span>
      </div>

      <div className="today-box__body">
        {error ? (
          <div className="empty-state">
            <p className="empty-state__title">Грешка при зареждане</p>
            <p className="empty-state__desc">{error.message}</p>
          </div>
        ) : unworked.length === 0 ? (
          <div className="empty-state">
            <p className="empty-state__title">Няма необработени</p>
            <p className="empty-state__desc">
              Всички оценени обекти вече са контактувани. Пуснете нов цикъл от Пайплайн.
            </p>
            <Link to="/pipeline" className="btn btn--primary btn--sm">
              Към Пайплайн
            </Link>
          </div>
        ) : (
          <div className="today-list">
            {unworked.map((lead) => (
              <TopUnworkedItem key={lead.id} lead={lead} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ReviewAlert({ count }: { count: number }) {
  if (count <= 0) {
    return null;
  }

  return (
    <div className="today-alert">
      <div className="today-alert__icon">
        <IconAdmin size={22} />
      </div>
      <div className="today-alert__content">
        <div className="today-alert__title">{count} обекта чакат проверка</div>
        <div className="today-alert__desc">
          Несигурни фирмени съвпадения в Търговския регистър или конфликт при групиране.
        </div>
      </div>
      <Link to="/admin" className="btn btn--sm btn--secondary">
        Прегледай
      </Link>
    </div>
  );
}

export function TodayPage() {
  const { data: lastSeen = null } = useLastSeen();
  const { data: since } = useSinceLastVisit(lastSeen);
  const { data: followUps = [] } = useFollowUpsDue();
  const { data: unworked = [], error: unworkedError } = useTopUnworked();
  const { data: openReviews = 0 } = useOpenReviewCount();

  const { selectedVertical, selectedCity } = useNicheContext();

  const formattedDate = new Intl.DateTimeFormat('bg-BG', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date());

  const cityName = selectedCity?.name_bg ?? 'Всички градове';
  const verticalName = selectedVertical?.name_bg ?? 'Всички ниши';
  const marketContext = `${cityName} · ${verticalName}`;

  return (
    <div className="today-dash">
      {/* Morning Header */}
      <header className="today-dash__header">
        <div className="today-dash__hero">
          <div className="today-dash__eyebrow">
            <span className="today-dash__date">{formattedDate}</span>
            <span className="today-dash__badge">{marketContext}</span>
          </div>
          <h1 className="today-dash__greeting">Сутрешен брифинг</h1>
          <p className="today-dash__lead">
            Ежедневен преглед на насрочените обаждания, най-силните нови потенциални клиенти и
            задачите за проверка.
          </p>
        </div>

        <div className="today-dash__quick-actions">
          <Link to="/leads" className="btn btn--primary btn--sm">
            <IconLeads size={15} />
            <span>Всички потенциални</span>
          </Link>
          <Link to="/kanban" className="btn btn--secondary btn--sm">
            <IconKanban size={15} />
            <span>Канбан табло</span>
          </Link>
          <Link to="/pipeline" className="btn btn--secondary btn--sm">
            <IconPipeline size={15} />
            <span>Пайплайн</span>
          </Link>
        </div>
      </header>

      {/* Top Metric Cards */}
      <TodayMetrics
        followUpsCount={followUps.length}
        unworkedCount={unworked.length}
        newLeads={since?.newLeads ?? 0}
        newDocuments={since?.newDocuments}
        openReviews={openReviews}
      />

      {/* Main Grid */}
      <div className="today-dash__grid">
        {/* Left Column: Scheduled Follow-ups */}
        <FollowUpsSection />

        {/* Right Column: Top Unworked & Review Alerts */}
        <div className="today-dash__col">
          <TopUnworkedSection unworked={unworked} error={unworkedError} />
          <ReviewAlert count={openReviews} />
        </div>
      </div>
    </div>
  );
}
